/**
 * Auto-context collection for the Holostaff SDK.
 *
 * Implements PRD §5.7: the privacy-conscious subset of Segment's
 * common-context fields that inform skill decisions. Page / campaign
 * / locale / timezone / screen / viewport / user_agent / device_type
 * / library / session entry are populated automatically on every SDK
 * payload.
 *
 * NOT collected (see §5.7.2): IP (server-side only), geolocation,
 * persistent fingerprint, network / bluetooth.
 */

export interface HolostaffContext {
  page?: {
    url?: string
    path?: string
    title?: string
    referrer?: string
    search?: string
  }
  campaign?: {
    source?: string
    medium?: string
    name?: string
    term?: string
    content?: string
  }
  locale?: string
  timezone?: string
  screen?: {
    width?: number
    height?: number
    density?: number
  }
  viewport?: {
    width?: number
    height?: number
  }
  user_agent?: string
  device_type?: 'desktop' | 'tablet' | 'mobile'
  library?: {
    name?: string
    version?: string
  }
  session?: {
    entry_page?: string
    entry_time?: string
  }
}

function parseCampaign(search: string): HolostaffContext['campaign'] {
  if (!search) return undefined
  const params = new URLSearchParams(search)
  const out: HolostaffContext['campaign'] = {}
  const source = params.get('utm_source')
  const medium = params.get('utm_medium')
  const name = params.get('utm_campaign')
  const term = params.get('utm_term')
  const content = params.get('utm_content')
  if (source) out.source = source
  if (medium) out.medium = medium
  if (name) out.name = name
  if (term) out.term = term
  if (content) out.content = content
  return Object.keys(out).length ? out : undefined
}

function deriveDeviceType(ua: string, viewportWidth: number): HolostaffContext['device_type'] {
  const lower = ua.toLowerCase()
  // Prefer explicit UA hints over viewport width.
  if (/iphone|android.*mobile|windows phone/.test(lower)) return 'mobile'
  if (/ipad|tablet|android(?!.*mobile)/.test(lower)) return 'tablet'
  // Viewport fallback.
  if (viewportWidth <= 640) return 'mobile'
  if (viewportWidth <= 1024) return 'tablet'
  return 'desktop'
}

/**
 * Snapshot of stable context fields taken once per session. Viewport
 * is returned too but callers should re-invoke `currentViewport()` on
 * window resize to keep `context.viewport.*` fresh.
 */
export function collectSessionContext(library: { name: string; version: string }): {
  context: HolostaffContext
  sessionEntryPage: string
  sessionEntryTime: string
} {
  const now = new Date().toISOString()
  const search = typeof location !== 'undefined' ? location.search : ''
  const title = typeof document !== 'undefined' ? document.title : ''
  const referrer = typeof document !== 'undefined' ? document.referrer : ''
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const locale = typeof navigator !== 'undefined' ? navigator.language : ''
  const tz =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : ''
  const screenW = typeof screen !== 'undefined' ? screen.width : 0
  const screenH = typeof screen !== 'undefined' ? screen.height : 0
  const density = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 0
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 0
  const entryPage = typeof location !== 'undefined' ? location.href : ''

  const context: HolostaffContext = {
    page: {
      url: entryPage,
      path: typeof location !== 'undefined' ? location.pathname : '',
      title,
      referrer,
      search,
    },
    campaign: parseCampaign(search),
    locale,
    timezone: tz,
    screen: { width: screenW, height: screenH, density },
    viewport: { width: viewportW, height: viewportH },
    user_agent: ua,
    device_type: deriveDeviceType(ua, viewportW),
    library,
    session: { entry_page: entryPage, entry_time: now },
  }

  return { context, sessionEntryPage: entryPage, sessionEntryTime: now }
}

/** Re-read viewport dimensions. Call on resize / orientation change. */
export function currentViewport(): { width: number; height: number } | null {
  if (typeof window === 'undefined') return null
  return { width: window.innerWidth, height: window.innerHeight }
}

/** Re-read page fields. Call on SPA route change or explicit Holostaff.page(). */
export function currentPage(): HolostaffContext['page'] {
  if (typeof location === 'undefined') return undefined
  return {
    url: location.href,
    path: location.pathname,
    title: typeof document !== 'undefined' ? document.title : '',
    search: location.search,
  }
}
